// Contrôle réutilisable « détection patient » — la partie intelligente commune
// au volet « Nouveau patient » et au volet « Nouveau RDV » : ligne de saisie
// unique avec détection auto (prénom, nom, téléphone, email), chips éditables au
// clic + inversion prénom/nom, et détection de doublons. N'embarque AUCUN bouton
// de création : chaque parent garde ses propres actions. État contrôlé par le
// parent (text/overrides/swapped/editing) pour permettre préremplissage et reset.
import { Sparkles, User, Smartphone, Phone, Mail, Users, ArrowLeftRight } from "lucide-react";
import { LABELS, formatLastContact, initials } from "@/lib/patientParse";

// Petite pastille éditable (chip détectée).
function Chip({ icon: Icon, label, value, red, onEdit, testKey, idPrefix }) {
  return (
    <button type="button" onClick={onEdit} data-testid={`${idPrefix}-chip-${testKey}`}
      className="text-left rounded-[10px] px-2.5 py-2 transition-colors"
      style={{ background: "#f7f7f5", border: `1px solid ${red ? "oklch(0.8 0.1 25)" : "#ebebe7"}` }}>
      <div className="flex items-center gap-1 text-[11px]" style={{ color: red ? "oklch(0.55 0.15 25)" : "var(--muted)" }}>
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className="text-[14px] font-semibold mt-0.5 truncate" style={{ color: red ? "oklch(0.5 0.16 25)" : "var(--ink)" }}>{value}</div>
    </button>
  );
}

// Saisie en place d'une chip.
function ChipInput({ label, val, setVal, onDone, idPrefix }) {
  return (
    <div className="rounded-[10px] px-2.5 py-2" style={{ background: "#fff", border: "1px solid #141821" }}>
      <div className="text-[11px]" style={{ color: "var(--muted)" }}>{label}</div>
      <input autoFocus data-testid={`${idPrefix}-chip-input`} value={val} onChange={(e) => setVal(e.target.value)} onBlur={onDone}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); onDone(); } if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onDone(); } }}
        className="w-full bg-transparent outline-none text-[14px] font-semibold mt-0.5" style={{ color: "var(--ink)" }} />
    </div>
  );
}

export default function PatientDetectForm({
  text, setText, overrides, setOverrides, swapped, setSwapped,
  editing, setEditing, editVal, setEditVal,
  fields, dups = [], inputRef,
  idPrefix = "np", showEmailChip = false, showHelp = true,
  dupActionLabel = "Ouvrir", onDupAction, onEnter, placeholder = LABELS.placeholder,
}) {
  const startEdit = (key, cur) => { setEditing(key); setEditVal(cur || ""); };
  const commitEdit = () => { if (editing) setOverrides((o) => ({ ...o, [editing]: editVal })); setEditing(null); };
  const phoneLabel = fields.phone ? (fields.phone.kind === "mobile" ? "Mobile" : "Fixe") : "Mobile";
  const hasPrenom = fields.prenom || overrides.prenom !== undefined;
  const hasNom = fields.nom || overrides.nom !== undefined;
  const onKey = (e) => { if (e.key === "Enter") { e.preventDefault(); onEnter?.(e); } };

  return (
    <>
      {/* Champ unique */}
      <div className="flex items-center gap-2.5 px-3" style={{ height: 56, border: "2px solid #141821", borderRadius: 12 }}>
        <Sparkles className="w-[18px] h-[18px] shrink-0" style={{ color: "#c9a400" }} />
        <input ref={inputRef} data-testid={`${idPrefix}-input`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey}
          aria-label={placeholder} placeholder={placeholder} className="flex-1 bg-transparent outline-none" style={{ fontSize: 17, color: "var(--ink)" }} />
      </div>
      {showHelp && <p className="text-[12.5px] mt-1.5" style={{ color: "#6b7180" }}>{LABELS.help}</p>}

      {/* Chips détectées */}
      <div className="grid grid-cols-3 gap-2 mt-3 relative group" data-testid={`${idPrefix}-chips`}>
        {editing === "prenom" ? <ChipInput idPrefix={idPrefix} label="Prénom" val={editVal} setVal={setEditVal} onDone={commitEdit} /> :
          hasPrenom && <Chip idPrefix={idPrefix} icon={User} testKey="prenom" label="Prénom" value={fields.prenom || "—"} onEdit={() => startEdit("prenom", fields.prenom)} />}
        {editing === "nom" ? <ChipInput idPrefix={idPrefix} label="Nom" val={editVal} setVal={setEditVal} onDone={commitEdit} /> :
          hasNom && <Chip idPrefix={idPrefix} icon={User} testKey="nom" label="Nom" value={fields.nom || "—"} onEdit={() => startEdit("nom", fields.nom)} />}
        {editing === "phone" ? <ChipInput idPrefix={idPrefix} label={phoneLabel} val={editVal} setVal={setEditVal} onDone={commitEdit} /> :
          fields.phone && <Chip idPrefix={idPrefix} icon={fields.phone.kind === "mobile" ? Smartphone : Phone} testKey="phone" label={fields.phone.incomplete ? "numéro incomplet" : phoneLabel} value={fields.phone.display} red={fields.phone.incomplete} onEdit={() => startEdit("phone", fields.phone.display)} />}
        {showEmailChip && (editing === "email" ? <ChipInput idPrefix={idPrefix} label="Email" val={editVal} setVal={setEditVal} onDone={commitEdit} /> :
          fields.email && <Chip idPrefix={idPrefix} icon={Mail} testKey="email" label="Email" value={fields.email} onEdit={() => startEdit("email", fields.email)} />)}
        {hasPrenom && hasNom && !editing && (
          <button data-testid={`${idPrefix}-swap`} title="Inverser prénom et nom" onClick={() => { setSwapped((s) => !s); setOverrides((o) => { const { prenom, nom, ...rest } = o; return rest; }); }}
            className="absolute opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center justify-center"
            style={{ left: "33.33%", top: 23, transform: "translate(-50%,-50%)", width: 24, height: 24, borderRadius: 99, background: "#141821", color: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }}>
            <ArrowLeftRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Doublons */}
      {dups.length > 0 && (
        <div className="mt-3 rounded-[12px] p-3" aria-live="polite" data-testid={`${idPrefix}-dups`} style={{ background: "oklch(0.96 0.04 70)", color: "oklch(0.42 0.11 55)" }}>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold mb-2"><Users className="w-4 h-4" />{LABELS.dupTitle}</div>
          <div className="space-y-1.5">
            {dups.map(({ patient: p, reason }) => (
              <div key={p.id} data-testid={`${idPrefix}-dup-${p.id}`} className="flex items-center gap-2.5 rounded-[10px] px-2.5 py-2" style={{ background: "#fff" }}>
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: "oklch(0.93 0.05 70)", color: "oklch(0.42 0.11 55)" }}>{initials(p.prenom, p.nom)}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold truncate" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</div>
                  <div className="text-[11.5px] truncate" style={{ color: "var(--muted)" }}>{reason}{p.dernier_contact ? ` · dernier contact ${formatLastContact(p.dernier_contact)}` : ""}</div>
                </div>
                <button data-testid={`${idPrefix}-dup-open-${p.id}`} onClick={() => onDupAction?.(p)} className="text-[12.5px] font-semibold underline shrink-0" style={{ color: "var(--ink)" }}>{dupActionLabel}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
